import { AxiosResponse } from 'axios'
import {
  CriarTratativaRequest,
  PesquisaComunicacaoRequest,
  ComunicacaoPaginadaResponse,
  ComunicacaoDetalhes,
  VinculoComunicacao,
  OrigemComunicacao,
  CampoPesquisaAvancada,
  PesquisaSalva,
  SalvarPesquisaRequest,
  SituacoesEspeciaisComunicacaoPayload,
  ExecutarAcaoComunicacaoRequest,
  AvaliarValidacaoIaRequest,
  ProximaSituacaoAcao,
  PreparacaoAcao,
  DespachoComentario,
  CriarDespachoComentarioRequest,
  AnexoTratativa,
  TramitarComunicacaoRequest,
  TramitacaoRealizadaResponse,
} from '../interfaces/comunicacao'
import BaseService from './base-service'

const API = process.env.REACT_APP_API_URL

const ComunicacaoService = {
  /**
   * Pesquisa comunicações com filtros.
   * Endpoint: POST /comunicacao/pesquisar
   */
  pesquisarComunicacoes(
    data: PesquisaComunicacaoRequest,
    page: number = 0,
    size: number = 20,
    sort: string = 'id,ASC'
  ): Promise<AxiosResponse<ComunicacaoPaginadaResponse>> {
    const comunicacaoService = new BaseService<ComunicacaoPaginadaResponse>()
    const url = `${API}api/comunicacao/pesquisar?page=${page}&size=${size}&sort=${sort}`
    return comunicacaoService.post(url, data as any)
  },

  /**
   * Obtém detalhes de uma comunicação.
   * Endpoint: GET /comunicacao/{id}/detalhes
   */
  obterDetalhes(
    id: number,
    tipo?: string
  ): Promise<AxiosResponse<ComunicacaoDetalhes>> {
    const comunicacaoService = new BaseService<ComunicacaoDetalhes>()
    const url = tipo
      ? `${API}api/comunicacao/${id}/detalhes?tipo=${tipo}`
      : `${API}api/comunicacao/${id}/detalhes`
    return comunicacaoService.get(url)
  },

  listarVinculos(id: number): Promise<AxiosResponse<VinculoComunicacao[]>> {
    const comunicacaoService = new BaseService<VinculoComunicacao[]>()
    return comunicacaoService.get(`${API}api/comunicacao/${id}/vinculos`)
  },

  /**
   * POST /comunicacao/{id}/vinculos — cria vínculos com as instâncias selecionadas (HU-009).
   */
  criarVinculos(id: number, instanciaIds: number[]): Promise<AxiosResponse<void>> {
    const service = new BaseService<void>()
    return service.post(`${API}api/comunicacao/${id}/vinculos`, { instanciaIds } as any)
  },

  /**
   * DELETE /comunicacao/{id}/vinculos — inativa vínculos selecionados (HU-009).
   * Axios DELETE com body requer config.data.
   */
  excluirVinculos(id: number, vinculoIds: number[]): Promise<AxiosResponse<void>> {
    const service = new BaseService<void>()
    return service.delete(`${API}api/comunicacao/${id}/vinculos`, { data: { vinculoIds } })
  },

  /**
   * Atualiza marcações de situação especial da instância da comunicação.
   * POST /comunicacao/{instancia}/situacoes-especiais
   */
  atualizarSituacoesEspeciaisInstancia(
    instanciaId: number,
    body: SituacoesEspeciaisComunicacaoPayload
  ): Promise<AxiosResponse<ComunicacaoDetalhes | void>> {
    const service = new BaseService<ComunicacaoDetalhes | void>()
    return service.post(
      `${API}api/comunicacao/${instanciaId}/situacoes-especiais`,
      body as any
    )
  },

  atualizarFavorito(
    instanciaId: number,
    favorita: boolean
  ): Promise<AxiosResponse<void>> {
    const service = new BaseService<void>()
    return service.put(`${API}api/comunicacao/${instanciaId}/favorito`, { favorita } as any)
  },

  listarAcoesComunicacao(instanciaId: number): Promise<AxiosResponse<ProximaSituacaoAcao[]>> {
    const service = new BaseService<ProximaSituacaoAcao[]>()
    return service.get(`${API}api/comunicacao/${instanciaId}/acoes`)
  },

  /**
   * Executa ação de fluxo da comunicação.
   * POST /comunicacao/{instancia}/acoes — body `{ acaoId, despacho?, tipoDespacho? }`.
   */
  executarAcaoComunicacao(
    instanciaId: number,
    body: ExecutarAcaoComunicacaoRequest
  ): Promise<AxiosResponse<ComunicacaoDetalhes | void>> {
    const service = new BaseService<ComunicacaoDetalhes | void>()
    return service.post(`${API}api/comunicacao/${instanciaId}/acoes`, body as any)
  },

  prepararAcaoComunicacao(
    instanciaId: number,
    acaoId: number
  ): Promise<AxiosResponse<PreparacaoAcao>> {
    const service = new BaseService<PreparacaoAcao>()
    return service.get(`${API}api/comunicacao/${instanciaId}/acoes/${acaoId}/preparacao`)
  },

  listarDespachosComentarios(
    instanciaId: number,
    asriId?: number | null
  ): Promise<AxiosResponse<DespachoComentario[]>> {
    const service = new BaseService<DespachoComentario[]>()
    const query = asriId ? `?asriId=${asriId}` : ''
    return service.get(`${API}api/comunicacao/${instanciaId}/despachos-comentarios${query}`)
  },

  criarDespachoComentario(
    instanciaId: number,
    body: CriarDespachoComentarioRequest
  ): Promise<AxiosResponse<void>> {
    const service = new BaseService<void>()
    return service.post(`${API}api/comunicacao/${instanciaId}/despachos-comentarios`, body as any)
  },

  listarAnexosTratativa(
    instanciaId: number,
    despachoId: number,
    nome?: string | null
  ): Promise<AxiosResponse<AnexoTratativa[]>> {
    const service = new BaseService<AnexoTratativa[]>()
    const query = nome?.trim() ? `?nome=${encodeURIComponent(nome.trim())}` : ''
    return service.get(`${API}api/comunicacao/${instanciaId}/despachos-comentarios/${despachoId}/anexos${query}`)
  },

  excluirComentario(
    instanciaId: number,
    despachoId: number
  ): Promise<AxiosResponse<void>> {
    const service = new BaseService<void>()
    return service.delete(`${API}api/comunicacao/${instanciaId}/despachos-comentarios/${despachoId}`)
  },

  // Bug #829857: 'M' = somente para mim, 'P' = público. Restrito ao autor do comentário.
  atualizarVisibilidadeComentario(
    instanciaId: number,
    despachoId: number,
    visibilidade: 'M' | 'P'
  ): Promise<AxiosResponse<void>> {
    const service = new BaseService<void>()
    return service.put(`${API}api/comunicacao/${instanciaId}/despachos-comentarios/${despachoId}/visibilidade`, { visibilidade } as any)
  },

  avaliarValidacaoIa(
    comunicacaoId: number,
    body: AvaliarValidacaoIaRequest
  ): Promise<AxiosResponse<void>> {
    const service = new BaseService<void>()
    return service.post(`${API}api/comunicacao/${comunicacaoId}/validacao-ia`, body as any)
  },

  /**
   * Lista origens de comunicação.
   * Endpoint: GET /atendimento/origens-comunicacao
   */
  listarOrigens(): Promise<AxiosResponse<OrigemComunicacao[]>> {
    const comunicacaoService = new BaseService<OrigemComunicacao[]>()
    return comunicacaoService.get(`${API}api/atendimento/origens-comunicacao`)
  },

  /**
   * Lista campos disponíveis para pesquisa avançada.
   * Endpoint: GET /comunicacao/campos-avancados
   */
  listarCamposAvancados(): Promise<AxiosResponse<CampoPesquisaAvancada[]>> {
    const comunicacaoService = new BaseService<CampoPesquisaAvancada[]>()
    return comunicacaoService.get(`${API}api/comunicacao/campos-avancados`)
  },

  // ========================= Pesquisas Salvas do Usuário =========================

  /**
   * Lista pesquisas salvas do usuário autenticado.
   * Endpoint: GET /comunicacao/pesquisas-usuario
   */
  listarPesquisasSalvas(): Promise<AxiosResponse<PesquisaSalva[]>> {
    const service = new BaseService<PesquisaSalva[]>()
    return service.get(`${API}api/comunicacao/pesquisas-usuario`)
  },

  /**
   * Carrega uma pesquisa salva com seus critérios.
   * Endpoint: GET /comunicacao/pesquisas-usuario/{id}
   */
  carregarPesquisa(id: number): Promise<AxiosResponse<PesquisaSalva>> {
    const service = new BaseService<PesquisaSalva>()
    return service.get(`${API}api/comunicacao/pesquisas-usuario/${id}`)
  },

  /**
   * Salva ou atualiza uma pesquisa do usuário.
   * Endpoint: POST /comunicacao/pesquisas-usuario
   */
  salvarPesquisa(data: SalvarPesquisaRequest): Promise<AxiosResponse<PesquisaSalva>> {
    const service = new BaseService<PesquisaSalva>()
    return service.post(`${API}api/comunicacao/pesquisas-usuario`, data as any)
  },

  /**
   * Exclui uma pesquisa do usuário.
   * Endpoint: DELETE /comunicacao/pesquisas-usuario/{id}
   */
  excluirPesquisa(id: number): Promise<AxiosResponse<void>> {
    const service = new BaseService<void>()
    return service.delete(`${API}api/comunicacao/pesquisas-usuario/${id}`)
  },

  criarTratativa(instanciaId: number, body: CriarTratativaRequest) {
    const service = new BaseService()
    return service.post(API + 'api/comunicacao/' + instanciaId + '/tratativas', body)
  },

  /**
   * HU-002 (OS33) — Tramitar Comunicação: 1 andamento + 1 despacho independente por dupla
   * selecionada. Desde a HU-044 a resposta traz a situação nova e um Processo MPRJ por dupla,
   * que a tela de sucesso exibe.
   */
  criarTramitacao(
    instanciaId: number,
    body: TramitarComunicacaoRequest,
  ): Promise<AxiosResponse<TramitacaoRealizadaResponse>> {
    const service = new BaseService<TramitacaoRealizadaResponse>()
    return service.post(`${API}api/comunicacao/${instanciaId}/tramitacao`, body as any)
  },

  buscarDestinatariosInternos(q: string) {
    const service = new BaseService()
    return service.get(API + 'api/usuarios/buscar?q=' + encodeURIComponent(q))
  },

  buscarAnalistasSuperiores(q: string) {
    const service = new BaseService()
    return service.get(API + 'api/usuarios/buscar-analise-superior?q=' + encodeURIComponent(q))
  },

  prepararTratativa(instanciaId: number, acaoId: number) {
    const service = new BaseService()
    return service.get(API + 'api/comunicacao/' + instanciaId + '/acoes/' + acaoId + '/preparacao')
  },

  inicializarUsuario() {
    const service = new BaseService()
    return service.post(API + 'api/usuarios/inicializar', {})
  },
}

export default ComunicacaoService
